import React, { useMemo } from "react";
import Image from "next/image";
import { InstructorDetail } from "@features/instructor/types";

import InstructorProduct from "./InstructorProduct";
import { useInstructor } from "../hooks/useInstructor";
import MobileInstructor from "./MobileInstructor";
import InstructorHeader from "./InstructorHeader";
import {
  createInstructorContentParserOptions,
  parseInstructorHtml,
} from "../helpers/instructorContentParser";
import InstructorAvatar from "./InstructorAvatar";
import InstructorDesktopSidebar from "./InstructorDesktopSidebar";
import InstructorStatus from "./InstructorStatus";
import { INSTRUCTOR_BG_SRC } from "../helpers/constants";

interface InstructorProps {
  instructor: InstructorDetail;
}

const InstructorLanding: React.FC<InstructorProps> = ({ instructor }) => {
  const htmlParserOptions = useMemo(
    () => createInstructorContentParserOptions(),
    []
  );

  const parsedBio = useMemo(
    () => parseInstructorHtml(instructor.content || "", htmlParserOptions),
    [instructor.content, htmlParserOptions]
  );

  const {
    showInstructorStatus,
    DegreeGrade,
    instructorStatistic,
    data,
    error,
    isLoading,
    instructorBg,
  } = useInstructor(instructor);

  const products = data?.products;
  const hasProducts = Boolean(products?.length);
  const showDesktopProducts = (isLoading && !error) || hasProducts;

  return (
    <>
      <InstructorHeader instrutorName={instructor.name} />
      <div className="fds-l:fds-container  pt-6   w-full px-4   fds-l:flex gap-x-6">
        <div className="detailInstructor grow shrink-0 w-full border-fds-cart border rounded-10 fds-l:px-0 fds-l:py-6">
          <div className="fds-l:flex fds-l:pb-6  fds-l:px-4     fds-l:flex-row relative w-full rounded-10 fds-l:rounded-0 flex-col items-start  fds-l:items-center ">
            <div
              className="w-full rounded-t-10 overflow-hidden fds-l:hidden relative   aspect-[4/1]"
              ref={instructorBg}
            >
              <Image
                fill
                src={INSTRUCTOR_BG_SRC}
                alt="bg-instructor"
                className="object-cover"
              />
            </div>
            <InstructorAvatar
              photo={instructor.photo}
              className="relative block fds-l:hidden  aspect-square  instructorPic "
            />
            <InstructorAvatar
              photo={instructor.photo}
              className="relative hidden fds-l:block  aspect-square  instructorPic "
            />

            <div className="flex fds-sm:py-5    fds-l:py-0 flex-col px-3  fds-l:px-4     mt-3">
              <h1 className="fds-l:headline-xlg headline-md fds-l:mb-2  mb-1">
                {instructor.name}
              </h1>
              <p className="fds-l:title-lg title-sm">
                {instructor.instructor_field}
              </p>
              <p className="body-md text-fds-secondary"></p>
              <p className="text-fds-tertiary body-sm fds-l:body-md">
                {DegreeGrade()}
              </p>
            </div>
          </div>
          {Boolean(showInstructorStatus) && (
            <InstructorStatus
              instructorStatistic={instructorStatistic}
              years={instructor.years}
              showYears={instructor.show_years}
            />
          )}
          {showDesktopProducts ? (
            <div className="hidden fds-l:block">
              <InstructorProduct
                products={products}
                id={String(instructor.id)}
              />
            </div>
          ) : null}
        </div>
        <div className="fds-l:hidden block">
          <MobileInstructor
            instructor={instructor}
            options={htmlParserOptions}
            products={products}
            productCount={data?.pagination.total_items || 0}
            degrees={instructor.instructor_educational_background}
          />
        </div>
        <InstructorDesktopSidebar
          instructor={instructor}
          parsedBio={parsedBio}
        />
      </div>
    </>
  );
};

export default InstructorLanding;
